import { DateTime } from 'luxon'
import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import isEqual from 'lodash/isEqual'
import cx from 'classnames'
import IntersectionObserver from '@researchgate/react-intersection-observer'

import { useAvailability } from '../../utils/availability'
import { useReorderQuery } from '../ReorderQuery/ReorderQuery'
import { useFlag } from '../FeatureFlag/use-flag'
import { cdnUrl } from '../../utils/cdn'
import { getFastRect } from '../../utils/dimensions'
import { formatPhoneNumber } from '../../utils/phone-number'

import InfoImg from '../../assets/info.svg'
import PhoneImg from '../../assets/phone.svg'

import MenusNav from '../MenusNav/MenusNav'
import RestaurantAddress from '../RestaurantAddress/RestaurantAddress'
import { FulfillmentSelector } from '../FulfillmentSelector/FulfillmentSelector'
import { MenuSelector } from '../MenuSelector/MenuSelector'
import Modal from '../Modal/Modal'
import { IfOutpost, UnlessOutpost } from '../IfOutpost/IfOutpost'
import { SocialMediaButtons } from '../SocialMediaButtons/SocialMediaButtons'
import { RestaurantNotification } from '../RestaurantNotification/RestaurantNotification'
import { ReordersOrPopularItemsBanner } from './ReordersOrPopularItemsBanner'

import './RestaurantHeader.css'

/**
 * @param {string} localTime ISO-formatted local time
 * @returns {string} formatted hours for display
 */
const formatHours = localTime => DateTime
  .fromISO(localTime)
  .toLocaleString(DateTime.TIME_SIMPLE)

/**
 * @param {object} schedule
 * @returns {string} like `Open X:XX AM - Y:YY PM`
 */
const getOpenString = schedule => {
  if (schedule.todaysHoursForTakeout) {
    return `Open ${formatHours(schedule.todaysHoursForTakeout.startTime)} - ${formatHours(schedule.todaysHoursForTakeout.endTime)}`
  }

  if (schedule.asapAvailableForTakeout) {
    // This shouldn't normally happen (asap available but no todaysHours)
    return 'Open'
  }

  return 'Closed'
}

const RestaurantHeader = ({
  restaurant: {
    guid,
    imageUrl,
    bannerUrls,
    minimumTakeoutTime,
    whiteLabelName,
    location,
    logoUrls = {},
    schedule,
    description,
    socialMediaLinks
  },
  menus,
  activeItem,
  imperativeItemHandler,
  publicApi
}) => {
  const { address1, address2, city, state, phone } = location
  const useBannerImage = useFlag('oo-v3-banner-image-fix')
  const scrollableMenuGroupEnabled = useFlag('oo-v3-scrollable-menugroup-nav')
  const bannerUrl = useBannerImage ? (bannerUrls && bannerUrls.raw) : (imageUrl && cdnUrl(imageUrl))
  const [shouldShowInfo, editShowInfo] = useState(false)
  const { orderingAvailable } = useAvailability()
  const stickyRef = useRef()
  const { hasReorders } = useReorderQuery()
  const [showStickyShadow, setShowStickyShadow] = useState(false)

  const calcHeaderOffset = async () => {
    const { height } = await getFastRect(stickyRef.current)
    return height
  }

  // Call directly from the React module to make this mockable
  React.useImperativeHandle(publicApi, () => {
    return {
      calcHeaderOffset
    }
  })

  const showStickyShadowCallback = (entry) => {
    setShowStickyShadow(!entry.isIntersecting)
  }

  return (
    <React.Fragment>
      <div styleName='header'>
        <div styleName='notification'>
          <RestaurantNotification />
        </div>
        <div styleName='banner'>

          <div styleName='information'>
            {logoUrls && logoUrls.small
              ? (<div styleName='logo' style={{ backgroundImage: `url(${logoUrls.small})` }} />)
              : (<div styleName='logo empty' />)
            }

            <h1 styleName='name'>{whiteLabelName}</h1>
            <p styleName='info'>
              <RestaurantAddress
                address1={address1}
                address2={address2}
                city={city}
                state={state}
              />
              <br />
              {getOpenString(schedule)}
              <button styleName='infoButton' onClick={() => editShowInfo(!shouldShowInfo)}>
                <InfoImg styleName='infoImage' />
              </button>
            </p>
          </div>
          {shouldShowInfo &&
            <Modal onClose={() => editShowInfo(false)} header={whiteLabelName}>
              <div styleName='moreInfo'>
                <div styleName='infoIcons'>
                  {socialMediaLinks && (
                    <SocialMediaButtons socialMediaLinks={socialMediaLinks} />
                  )}
                  {phone && (
                    <div styleName='phone'>
                      <PhoneImg styleName='phoneImage' />
                      {formatPhoneNumber(phone)}
                    </div>
                  )}
                </div>
                {description && <p styleName='description'>{description}</p>}
              </div>
            </Modal>
          }
          {bannerUrl
            ? <div styleName='bannerImage' style={{ backgroundImage: `url(${bannerUrl})` }} />
            : <div styleName='noBannerImage' />
          }

        </div>
      </div>
      <IfOutpost>
        <div styleName='fulfillment'>
          <div styleName='fulfillmentDisplay'>
            <FulfillmentSelector minimumTakeoutTime={minimumTakeoutTime} />
          </div>
        </div>
      </IfOutpost>
      <UnlessOutpost>
        <div styleName='fulfillment'>
          {orderingAvailable &&
            <div styleName='fulfillmentDisplay'>
              <FulfillmentSelector minimumTakeoutTime={minimumTakeoutTime} />
            </div>
          }
        </div>
      </UnlessOutpost>
      <ReordersOrPopularItemsBanner hasReorders={hasReorders} />
      <IntersectionObserver
        // rootMargin -60px is the height of fixed header plus 5px to trigger the onChange
        rootMargin='-65px 0px 0px 0px'
        onChange={showStickyShadowCallback}
      >
        <div styleName='stickyShadowSentinel' />
      </IntersectionObserver>
      <div ref={stickyRef} styleName={cx('sticky', { stickyShadow: showStickyShadow && scrollableMenuGroupEnabled })}>
        <div styleName={cx('pageWidth', { scrollableMenuGroupEnabled })}>
          <MenuSelector imperativeItemHandler={imperativeItemHandler} activeMenu={menus} calcHeaderOffset={calcHeaderOffset} />
          <div styleName='nav'>
            <MenusNav menus={menus.groups} activeItem={activeItem} />
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

const LocationShape = PropTypes.shape({
  address1: PropTypes.string,
  address2: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  phone: PropTypes.string
})

const ImageSetShape = PropTypes.shape({
  raw: PropTypes.string
})

const SocialMediaLinksShape = PropTypes.shape({
  twitterLink: PropTypes.string,
  facebookLink: PropTypes.string
})

const RestaurantShape = PropTypes.shape({
  location: LocationShape,
  guid: PropTypes.string,
  imageUrl: PropTypes.string,
  bannerUrls: ImageSetShape,
  minimumTakeoutTime: PropTypes.number,
  name: PropTypes.string,
  socialMediaLinks: SocialMediaLinksShape
})

const PublicApiShape = PropTypes.shape({
  calcHeaderOffset: PropTypes.func
})

RestaurantHeader.propTypes = {
  restaurant: RestaurantShape,
  menus: PropTypes.object.isRequired,
  activeItem: PropTypes.string,
  imperativeItemHandler: PropTypes.func.isRequired,
  publicApi: PropTypes.shape({ current: PublicApiShape })
}

export default React.memo(RestaurantHeader, isEqual)
